import { pickBy, merge } from 'lodash-es'

import { createHttpClient } from '@sdk/utils/httpFetch'
import type { PitcherEnv } from '@sdk/interfaces'
import type { GetCanvasesParams } from '@lib/types/canvases'
import type { CanvasCreateRequest, CanvasRetrieve } from '@openapi'
import { normalizeNetworkFilterParams } from '@lib/components/filters/filter.util'

type ApiBaseUrlOverride = {
  api_base_url?: string
}

let env: PitcherEnv
let http: ReturnType<typeof createHttpClient>

function check() {
  if (!env) throw new Error('Missing env')
  if (!http) throw new Error('Missing http')
}

function pickNonNullish(data?: Record<string, any>) {
  return pickBy(data ?? {}, (v) => v !== undefined && v !== null)
}

export const setEnv = (e: PitcherEnv) => {
  if (!e?.pitcher?.token_claims?.['https://pitcher.com/claims/urls']?.custom_domain) {
    console.warn('No custom domain found in token claims', e?.pitcher?.token_claims)
    typeof window !== 'undefined' &&
      window.Sentry?.captureMessage?.('No custom domain found in token claims', {
        extra: { env: e },
        level: 'warning',
      })
  }

  const newCustomDomain = e?.pitcher?.token_claims?.['https://pitcher.com/claims/urls']?.custom_domain
  const shouldRefreshHttpClient =
    e?.pitcher?.access_token !== env?.pitcher?.access_token ||
    (newCustomDomain &&
      newCustomDomain !== env?.pitcher?.token_claims?.['https://pitcher.com/claims/urls']?.custom_domain)

  env = merge({}, env, e)

  if (shouldRefreshHttpClient) {
    http = createHttpClient(
      env.pitcher.token_claims['https://pitcher.com/claims/urls'].custom_domain,
      env.pitcher.access_token,
    )
  }
}

export const onlineRestApiHandlers: Record<string, any> = {
  get_canvases: async (
    params: GetCanvasesParams & { filters?: Record<string, any>; fields?: string } & ApiBaseUrlOverride,
  ) => {
    check()
    const { api_base_url, filters = {}, ...restParams } = params
    const res = await http.get('/canvases', {
      params: {
        instance_id: env.pitcher.instance.id,
        ...normalizeNetworkFilterParams(filters),
        ...pickNonNullish(restParams),
      },
    })

    return res
  },
  get_canvas: async (params: { id: CanvasRetrieve['id']; fields?: string; lazy_sections?: boolean }) => {
    check()
    const { id, ...restParams } = params
    const res = await http.get(`/canvases/${params.id}`, { params: restParams })
    return res
  },
  // Lazy section loading companion: batch-hydrate section bodies for a canvas
  // that was fetched as a shell (`?lazy_sections=true`). Hits the same Django
  // /api/v1 base as get_canvas. Returns { sections: CanvasSection[] }.
  get_canvas_sections_by_ids: async (params: {
    canvas_id: CanvasRetrieve['id']
    section_ids: string[]
    // Forwarded so hydration filters identically to the shell that advertised
    // the IDs (admin shells are fetched with exclude_drafts:false + expired/
    // pending on). Only sent when provided; the endpoint applies rep defaults
    // and role-gates expired/pending otherwise.
    exclude_drafts?: boolean
    include_expired_files?: boolean
    include_pending_files?: boolean
  }) => {
    check()
    const res = await http.post('/canvases/sections/by-ids', {
      canvas_id: params.canvas_id,
      section_ids: params.section_ids,
      ...(params.exclude_drafts !== undefined && { exclude_drafts: params.exclude_drafts }),
      ...(params.include_expired_files !== undefined && { include_expired_files: params.include_expired_files }),
      ...(params.include_pending_files !== undefined && { include_pending_files: params.include_pending_files }),
    })
    return res
  },
  create_canvas: async (params: CanvasCreateRequest) => {
    check()
    const res = await http.post('/canvases', { ...params, instance_id: env.pitcher.instance.id })
    return res
  },
  update_canvas: async (params: { id: CanvasRetrieve['id']; fields?: string } & Record<string, any>) => {
    check()
    const { id, fields, ...canvasData } = params
    const useCoreEndpoint = (env as any)?.launch_darkly?.enable_canvas_core_patch === true
    if (useCoreEndpoint) {
      const origin = env.pitcher.token_claims['https://pitcher.com/claims/urls'].custom_domain
      const fieldsQs = fields ? `?fields=${encodeURIComponent(fields)}` : ''
      const url = `${origin}/core/api/protected/v1/canvases/${id}${fieldsQs}`
      // Use http.patchUrl for Core path (bypasses /api/v1 prefix)
      const res = await http.patchUrl(url, canvasData)
      return res
    }
    const res = await http.patch(`/canvases/${id}`, { fields, ...canvasData })
    return res
  },
  delete_canvas: async (params: { id: CanvasRetrieve['id'] }) => {
    check()
    const res = await http.delete(`/canvases/${params.id}`)
    return res
  },
  share_canvas: async (params: { id: CanvasRetrieve['id'] }) => {
    check()
    let link

    try {
      link = await http.get(`/shared-links/canvas/${params.id}`)
    } catch (e) {
      if ((e as Error & { status?: number })?.status === 404) {
        link = await http.post('/shared-links', {
          canvas_id: params.id,
        })
      } else {
        throw e
      }
    }

    return link
  },
  get_canvas_recommended_files: async (params: {
    canvas_id: CanvasRetrieve['id']
    match?: ('tags' | 'metadata')[]
  }) => {
    check()
    const { canvas_id, ...restParams } = params
    const res = await http.get(`/canvases/${canvas_id}/recommended_files`, { params: restParams })
    return res
  },
  get_collaborations: async (params: Record<string, any>) => {
    check()
    const res = await http.get('/collaborations', { params: pickNonNullish(params) })
    return res
  },
  create_collaboration: async (params: Record<string, any>) => {
    check()
    const res = await http.post('/collaborations', params)
    return res
  },
  delete_collaboration: async (params: { collaboration_id: number | string }) => {
    check()
    const { collaboration_id } = params
    const res = await http.delete(`/collaborations/${collaboration_id}`)
    return res
  },
  get_instance_memberships: async (params: Record<string, any>) => {
    check()
    const res = await http.get('/instance-memberships', { params: pickNonNullish(params) })
    return res
  },
  get_content_distribution_groups: async (params: Record<string, any>) => {
    check()
    const res = await http.get('/content-distribution-groups', { params: pickNonNullish(params) })
    return res
  },
}
